import { useState } from 'react';
import { previewEmailImport, applyEmailImport } from '../../services/api';

const FIELD = 'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';

// Importing parent emails from the Stripe export.
//
// A Stripe export is billing data: the name on it is whoever paid, not the
// child, and one payer covers siblings. So this never writes anything on its
// own — it proposes, the admin confirms, and only ticked rows are saved.

function Row({ entry, choice, onChoose }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-900">{entry.email}</div>
          <div className="truncate text-xs text-gray-500">
            paid as “{entry.name || 'no name given'}” — {entry.reason}
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {entry.students.map((student) => {
          const active = choice === student.id;
          return (
            <button
              key={student.id}
              onClick={() => onChoose(active ? null : student.id)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                active
                  ? 'border-purple-600 bg-purple-600 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {student.displayName}
              {student.parentEmails?.length > 0 && ' · has an email'}
            </button>
          );
        })}
        <button
          onClick={() => onChoose(null)}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
            choice == null ? 'border-gray-300 bg-gray-100 text-gray-700' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

export default function EmailImport({ onClose, onImported }) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const handlePreview = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await previewEmailImport(text);
      setPreview(data);
      // Confident matches start ticked; anything ambiguous starts unticked, so
      // a wrong guess needs a deliberate click rather than an unnoticed one.
      const initial = {};
      data.matched.forEach((entry) => { initial[entry.email] = entry.students[0].id; });
      setChoices(initial);
    } catch (previewError) {
      setError(previewError.response?.data?.error || 'Could not read that export');
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    const assignments = Object.entries(choices)
      .filter(([, studentProfileId]) => studentProfileId)
      .map(([email, studentProfileId]) => ({ email, studentProfileId }));

    if (assignments.length === 0) { setError('Nothing is ticked to import'); return; }

    setBusy(true);
    setError('');
    try {
      const { data } = await applyEmailImport(assignments);
      setDone(data);
      onImported?.();
    } catch (applyError) {
      setError(applyError.response?.data?.error || 'Could not import these addresses');
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = Object.values(choices).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
      <div className="relative h-full w-full overflow-y-auto bg-white shadow-2xl sm:max-w-lg">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Import parent emails</h2>
          <button onClick={onClose} className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">
            Close
          </button>
        </div>

        <div className="space-y-4 p-5">
          {done ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-medium text-green-900">
                Added {done.applied} {done.applied === 1 ? 'address' : 'addresses'}.
              </p>
              {done.failed?.length > 0 && (
                <p className="mt-1 text-xs text-red-700">{done.failed.length} could not be saved.</p>
              )}
              <p className="mt-2 text-xs text-green-800">
                You can now send their invitations from the student list.
              </p>
              <button onClick={onClose} className="mt-3 rounded-md bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800">
                Done
              </button>
            </div>
          ) : (
            <>
              <div>
                <p className="mb-2 text-sm text-gray-600">
                  Paste the Stripe export — name and email columns, with or without headings.
                  Nothing is saved until you confirm each match.
                </p>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={6}
                  placeholder={'Name,Email\nHarpreet Kooner,harpreet@example.com'}
                  className={`${FIELD} font-mono text-xs`}
                />
                <button
                  onClick={handlePreview}
                  disabled={busy || !text.trim()}
                  className="mt-2 rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? 'Reading…' : 'Match against students'}
                </button>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              {preview && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                    {preview.total} rows read · {preview.matched.length} matched ·
                    {' '}{preview.ambiguous.length} need a choice · {preview.unmatched.length} unmatched
                    {preview.skipped.length > 0 && ` · ${preview.skipped.length} skipped`}
                  </div>

                  {preview.matched.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                        Matched — ticked, untick any that look wrong
                      </h3>
                      <div className="space-y-2">
                        {preview.matched.map((entry) => (
                          <Row
                            key={entry.email}
                            entry={entry}
                            choice={choices[entry.email]}
                            onChoose={(id) => setChoices((c) => ({ ...c, [entry.email]: id }))}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.ambiguous.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                        Need a choice — often a parent of siblings
                      </h3>
                      <div className="space-y-2">
                        {preview.ambiguous.map((entry) => (
                          <Row
                            key={entry.email}
                            entry={entry}
                            choice={choices[entry.email]}
                            onChoose={(id) => setChoices((c) => ({ ...c, [entry.email]: id }))}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.unmatched.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                        No student found ({preview.unmatched.length})
                      </h3>
                      <div className="rounded-lg border border-dashed border-gray-200 p-3 text-xs text-gray-500">
                        {preview.unmatched.slice(0, 8).map((entry) => (
                          <div key={entry.email} className="truncate">{entry.email} — “{entry.name}”</div>
                        ))}
                        {preview.unmatched.length > 8 && <div>…and {preview.unmatched.length - 8} more</div>}
                        <p className="mt-2 text-gray-400">
                          Usually a payer whose name doesn't match the child's. Add these by hand.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-gray-200 bg-white py-3">
                    <span className="text-sm text-gray-600">{selectedCount} ready to import</span>
                    <button
                      onClick={handleApply}
                      disabled={busy || selectedCount === 0}
                      className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busy ? 'Importing…' : `Import ${selectedCount}`}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
